import { useSyncExternalStore } from 'react';

// Reading preferences are a pure client concern (no server round-trip needed
// to render a font size), so they live in localStorage. Synced across tabs
// via the storage event through useSyncExternalStore.

export interface ReadingPreferences {
  fontScale: number; // rem multiplier for the article body
  lineHeight: number;
}

const KEY = 'archetelos-reading-prefs';
const DEFAULTS: ReadingPreferences = { fontScale: 1.0625, lineHeight: 1.75 };

function read(): ReadingPreferences {
  if (typeof window === 'undefined') return DEFAULTS;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<ReadingPreferences>;
    return {
      fontScale: typeof parsed.fontScale === 'number' ? parsed.fontScale : DEFAULTS.fontScale,
      lineHeight: typeof parsed.lineHeight === 'number' ? parsed.lineHeight : DEFAULTS.lineHeight,
    };
  } catch {
    return DEFAULTS;
  }
}

const listeners = new Set<() => void>();
let cache = typeof window === 'undefined' ? DEFAULTS : read();

function emit() {
  cache = read();
  listeners.forEach((l) => l());
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === KEY) emit();
  });
}

export function setReadingPreferences(next: Partial<ReadingPreferences>) {
  const merged = { ...read(), ...next };
  window.localStorage.setItem(KEY, JSON.stringify(merged));
  emit();
}

export function useReadingPreferences(): ReadingPreferences {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => cache,
    () => DEFAULTS,
  );
}
